import { BusinessEntityType } from '@grc/ui-shared/lib/components/models/enums/enums';
import I18N from '../../i18n/interface';
import {
  BusinessEntity,
  BusinessEntityMap,
  EquipmentMap,
  GridRow,
  ModeMap,
  ServiceLevelMap,
  ShipmentTypeMap
} from '../../common/constants/constants';
import { getBusinessEntities } from './get-business-entities';
import { fetchBusinessEntities } from './fetch-business-entities';

export function formatDate(iso?: string | null): string {
  if (!iso) return '';
  if (iso === '1970-01-01T00:00:00Z') return 'Not Set';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const datePart = d.toLocaleDateString(undefined, {
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    });
    const timePart = d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    return `${datePart} | ${timePart}`;
  } catch {
    return '';
  }
}

export const formatWhen = (iso?: string, tz?: string) => {
  if (!iso) return '-';
  const z = tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const d = new Date(iso);
  const dtf = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: z,
    timeZoneName: 'short'
  });
  return dtf.format(d).replace(/, (?=[^,]*$)/, ' | ');
};

export const TenderSourceLabels: Record<number, string> = {
  0: 'Regular Tender',
  1: 'Expedited Tender',
  2: 'Spot Bid'
};

export const getTransportStatusLabels = (
  localeData: I18N
): Record<number, string> => ({
  0: localeData.Unknown,
  1: localeData.Created,
  2: localeData.Tendered,
  3: localeData.PrePickup,
  4: localeData.AtStop,
  5: localeData.InTransit,
  6: localeData.Completed,
  7: localeData.Closed,
  8: localeData.Cancelled,
  9: localeData.PostedForBid
});

export const TransportStatusLabels: Record<number, string> = {
  0: 'Unknown',
  1: 'Created',
  2: 'Tendered',
  3: 'Pre-Pickup',
  4: 'At Stop',
  5: 'In Transit',
  6: 'Completed',
  7: 'Closed',
  8: 'Cancelled',
  9: 'Posted For Bid'
};

export const TenderStatusLabels: Record<number, string> = {
  0: 'Unknown',
  1: 'Created',
  2: 'RateRequested',
  3: 'BidRequired',
  4: 'SubmittedForSending',
  5: 'Sent',
  6: 'NotAccepted',
  7: 'Accepted',
  8: 'SubmittedForCancelling',
  9: 'Cancelled'
};

export const BidStatusLabels: Record<number, string> = {
  0: 'Unknown',
  1: 'Created',
  2: 'Accepted',
  3: 'UnderReview',
  4: 'Awarded',
  5: 'Rejected',
  6: 'Withdrawn',
  7: 'Expired'
};

export const InvoiceMethodLabels: Record<number, string> = {
  0: 'Select',
  1: 'Edi',
  2: 'Api',
  3: 'Email'
};

export const BillingDaysLabels: Record<number, string> = {
  1: '15Days',
  2: '30Days',
  3: '45Days',
  4: '60Days'
};

export const EdiProcessingStatusLabels: Record<number, string> = {
  0: 'Received',
  1: 'Date Validation Failed',
  2: 'Parsing Failed',
  3: 'Processing Failed',
  4: 'Processing Completed',
  5: 'Completed'
};

export const TransportModeLabels: Record<number, string> = {
  1: 'Less Than Truckload',
  2: 'Truckload',
  3: 'Rail',
  4: 'Air',
  5: 'Ocean',
  6: 'Intermodal',
  7: 'All'
};

export const ShipmentSourceLabels: Record<number, string> = {
  1: 'Manual',
  2: 'Scan'
};

export const InvoiceStatusLabels: Record<number, string> = {
  0: 'Draft',
  1: 'Pending',
  2: 'Approved',
  3: 'Rejected'
};

export const InsuranceTypeLabels: Record<number, string> = {
  1: 'Cargo',
  2: 'Auto',
  3: 'CargoLiability',
  4: 'MotorTruck',
  5: 'Damage',
  6: 'Marine',
  7: 'AirOrAviation',
  8: 'Other'
};

export const mapToGridRows = (
  items: any[],
  beMap: BusinessEntityMap,
  modeMap: ModeMap,
  equipmentMap: EquipmentMap
): GridRow[] =>
  items.map(x => ({
    transportId: x.transportId,
    tenderId: x.transportNumber,
    status: TransportStatusLabels[x.status] ?? String(x.status),
    source: TenderSourceLabels[x.source] ?? String(x.source),
    mode:
      modeMap[x.transportationModeId]?.displayName || x.transportationModeId,
    originId: x.originId,
    destinationId: x.destinationId,
    origin: beMap[x.originId]?.name || 'N/A',
    originDisplayId: beMap[x.originId]?.displayId || 'N/A',
    originAddress: beMap[x.originId]?.address || 'N/A',
    originStreetAddress:
      beMap[x.originId]?.address?.streetAddress?.[0] || 'N/A',
    originCity: beMap[x.originId]?.address?.city || 'N/A',
    originState: beMap[x.originId]?.address?.provinceState || 'N/A',
    originPostalCode: beMap[x.originId]?.address?.postalZipCode || 'N/A',
    originPhone: beMap[x.originId]?.generalPhoneNumber?.phoneNumber || 'N/A',
    originEmail: beMap[x.originId]?.generalEmailAddress || 'N/A',
    destination: beMap[x.destinationId]?.name || 'N/A',
    destinationDisplayId: beMap[x.destinationId]?.displayId || 'N/A',
    destinationAddress: beMap[x.destinationId]?.address || 'N/A',
    destinationStreetAddress:
      beMap[x.destinationId]?.address?.streetAddress?.[0] || 'N/A',
    destinationCity: beMap[x.destinationId]?.address?.city || 'N/A',
    destinationState: beMap[x.destinationId]?.address?.provinceState || 'N/A',
    destinationPostalCode:
      beMap[x.destinationId]?.address?.postalZipCode || 'N/A',
    destinationPhone:
      beMap[x.destinationId]?.generalPhoneNumber?.phoneNumber || 'N/A',
    destinationEmail: beMap[x.destinationId]?.generalEmailAddress || 'N/A',
    transportationMode:
      modeMap[x.transportationModeId]?.displayName || x.transportationModeId,
    transportationModeId: x.transportationModeId,
    puDateAndTime: formatDate(x.pickupDateTime) || 'N/A', // <- uses new field
    deliveryDateAndTime: formatDate(x.deliveryDateTime) || 'N/A', // <- API uses 'Dat'
    plannedStart: formatDate(x.plannedStart) || 'N/A',
    plannedEnd: formatDate(x.plannedEnd) || 'N/A',
    equipmentName: equipmentMap[x.equipmentId]?.equipmentTypeName || 'N/A',
    carrier: x.carrier ?? null,
    nextLocationUpdate: formatDate(x.nextLocationUpdate) || 'N/A',
    carrierPro: x.carrierPro ?? 'N/A',
    trailerNo: x.trailerNo ?? 'N/A',
    noOfShipments: x.noOfShipments ?? 0,
    quantity: x.quantity ?? 0,
    weight: x.weight ?? 0
  }));

export const mapToAllTenderGridRows = (
  items: any[],
  beMap: BusinessEntityMap,
  modeMap: ModeMap,
  equipmentMap: EquipmentMap,
  shipmentTypeMap: ShipmentTypeMap,
  serviceLevelMap: ServiceLevelMap,
  billToMap: BusinessEntityMap
): GridRow[] =>
  items.map(x => ({
    id: x.tenderId,
    transportId: x.transportId,
    tenderId: x.transportNumber ?? '-',
    status:
      TenderStatusLabels[x.latestCarrier?.status?.tenderStatus] ??
      TenderStatusLabels['0'],
    source: TenderSourceLabels[x.source] ?? String(x.source),
    mode: modeMap[x.modeId]?.displayName || x.modeId,
    activeCarrier: x.latestCarrier,
    originId: x.originId,
    destinationId: x.destinationId,
    origin: beMap[x.originId]?.name || 'N/A',
    originDisplayId: beMap[x.originId]?.displayId || 'N/A',
    originAddress: beMap[x.originId]?.address || 'N/A',
    originStreetAddress:
      beMap[x.originId]?.address?.streetAddress?.[0] || 'N/A',
    originCity: beMap[x.originId]?.address?.city || 'N/A',
    originState: beMap[x.originId]?.address?.provinceState || 'N/A',
    originPostalCode: beMap[x.originId]?.address?.postalZipCode || 'N/A',
    originPhone: beMap[x.originId]?.generalPhoneNumber?.phoneNumber || 'N/A',
    originEmail: beMap[x.originId]?.generalEmailAddress || 'N/A',
    destination: beMap[x.destinationId]?.name || 'N/A',
    destinationDisplayId: beMap[x.destinationId]?.displayId || 'N/A',
    destinationAddress: beMap[x.destinationId]?.address || 'N/A',
    destinationStreetAddress:
      beMap[x.destinationId]?.address?.streetAddress?.[0] || 'N/A',
    destinationCity: beMap[x.destinationId]?.address?.city || 'N/A',
    destinationState: beMap[x.destinationId]?.address?.provinceState || 'N/A',
    destinationPostalCode:
      beMap[x.destinationId]?.address?.postalZipCode || 'N/A',
    destinationPhone:
      beMap[x.destinationId]?.generalPhoneNumber?.phoneNumber || 'N/A',
    destinationEmail: beMap[x.destinationId]?.generalEmailAddress || 'N/A',
    transportationMode: modeMap[x.modeId]?.displayName || x.modeId,
    transportationModeId: x.modeId,
    equipmentName: equipmentMap[x.equipmentId]?.equipmentTypeName || 'N/A',
    carriers: x.carriers ?? [],
    billToId: x.billToId,
    billToName: billToMap[x.billToId]?.name || 'N/A',
    billToDisplayId: billToMap[x.billToId]?.displayId || 'N/A',
    billToAddress: billToMap[x.billToId]?.address || 'N/A',
    billToStreetAddress:
      billToMap[x.billToId]?.address?.streetAddress?.[0] || 'N/A',
    billToCity: billToMap[x.billToId]?.address?.city || 'N/A',
    billToState: billToMap[x.billToId]?.address?.provinceState || 'N/A',
    billToPostalCode: billToMap[x.billToId]?.address?.postalZipCode || 'N/A',
    billToPhone:
      billToMap[x.billToId]?.generalPhoneNumber?.phoneNumber || 'N/A',
    billToEmail: billToMap[x.billToId]?.generalEmailAddress || 'N/A',
    isTeamServiceLevel: x.isTeamServiceLevel ? 'Team' : 'Solo',
    serviceLevelName:
      serviceLevelMap[x.serviceLevelId]?.serviceChargeName ?? 'N/A',
    shipmentTypeName:
      shipmentTypeMap[x.shipmentTypeId]?.shipmentTypeName ?? 'N/A',
    carrierPro: x.carrierPro ?? 'N/A',
    trailerNo: x.trailerNo ?? 'N/A',
    noOfShipments: x.noOfShipments ?? 0,
    quantity: x.quantity ?? 0,
    weight: x.weight ?? 0
  }));

// export const mapToTenderGridRows = (
//   items: any[],
//   beMap: BusinessEntityMap,
//   modeMap: ModeMap,
//   equipmentMap: EquipmentMap,
//   fetchData: any
// ): Promise<TenderGridRow[]> =>
//   Promise.all(
//     items.map(async x => {
//       // Resolve transport/tender number explicitly so we can inspect/fallback if needed
//       // Ensure `tenderId` is a string to match `TenderGridRow` type
//       let tenderId: string = x.tenderId ?? '';
//       try {
//         const fetched = await GetTransportNumberById({
//           transportId: x.transportId,
//           fetchData
//         });
//         // If helper returns a non-null/empty value, prefer it
//         if (fetched) tenderId = fetched;
//         else tenderId = '-';
//       } catch (err) {
//         // Keep original tenderId if present, otherwise null
//         console.error(
//           'Error in GetTransportNumberById for',
//           x.transportId,
//           err
//         );
//       }

//       return {
//         transportId: x.transportId,
//         tenderId,
//         status:
//           TenderStatus[x.latestCarrier.status.tenderStatus] ??
//           String(x.latestCarrier.status.tenderStatus),
//         mode: modeMap[x.modeId]?.name || x.modeId,
//         originId: x.originId,
//         destinationId: x.destinationId,
//         origin: beMap[x.originId]?.name || 'N/A',
//         originDisplayId: beMap[x.originId]?.displayId || 'N/A',
//         originAddress: beMap[x.originId]?.address || 'N/A',
//         originStreetAddress:
//           beMap[x.originId]?.address?.streetAddress?.[0] || 'N/A',
//         originCity: beMap[x.originId]?.address?.city || 'N/A',
//         originState: beMap[x.originId]?.address?.provinceState || 'N/A',
//         originPostalCode: beMap[x.originId]?.address?.postalZipCode || 'N/A',
//         originPhone:
//           beMap[x.originId]?.generalPhoneNumber?.phoneNumber || 'N/A',
//         originEmail: beMap[x.originId]?.generalEmailAddress || 'N/A',
//         destination: beMap[x.destinationId]?.name || 'N/A',
//         destinationDisplayId: beMap[x.destinationId]?.displayId || 'N/A',
//         destinationAddress: beMap[x.destinationId]?.address || 'N/A',
//         destinationStreetAddress:
//           beMap[x.destinationId]?.address?.streetAddress?.[0] || 'N/A',
//         destinationCity: beMap[x.destinationId]?.address?.city || 'N/A',
//         destinationState:
//           beMap[x.destinationId]?.address?.provinceState || 'N/A',
//         destinationPostalCode:
//           beMap[x.destinationId]?.address?.postalZipCode || 'N/A',
//         destinationPhone:
//           beMap[x.destinationId]?.generalPhoneNumber?.phoneNumber || 'N/A',
//         destinationEmail: beMap[x.destinationId]?.generalEmailAddress || 'N/A',
//         transportationMode: modeMap[x.modeId]?.name || x.modeId,
//         transportationModeId: x.modeId,
//         carriers: x.carriers ?? [],
//         latestCarrier: x.latestCarrier ?? null,
//         equipmentName: equipmentMap[x.equipmentId]?.equipmentTypeName || 'N/A'
//       };
//     })
//   );

export const extractIds = (list: any[]) => {
  const originIds = new Set<string>();
  const destinationIds = new Set<string>();
  const billToIds = new Set<string>();
  const modeIds = new Set<string>();
  const equipmentIds = new Set<string>();
  const shipmentTypeIds = new Set<string>();
  const serviceLevelIds = new Set<string>();
  for (const item of list) {
    if (item.originId) originIds.add(item.originId);
    if (item.destinationId) destinationIds.add(item.destinationId);
    if (item.billToId) billToIds.add(item.billToId);
    if (item.modeId) modeIds.add(item.modeId);
    if (item.equipmentId) equipmentIds.add(item.equipmentId);
    if (item.shipmentTypeId) shipmentTypeIds.add(item.shipmentTypeId);
    if (item.serviceLevelId) serviceLevelIds.add(item.serviceLevelId);
  }
  return {
    originIds: Array.from(originIds),
    destinationIds: Array.from(destinationIds),
    billToIds: Array.from(billToIds),
    modeIds: Array.from(modeIds),
    equipmentIds: Array.from(equipmentIds),
    shipmentTypeIds: Array.from(shipmentTypeIds),
    serviceLevelIds: Array.from(serviceLevelIds)
  };
};

export const fetchBusinessEntitiesByEntityType = async (
  entityIds: string[],
  fetchData: any,
  entityType: BusinessEntityType
) => {
  if (!entityIds.length) return {};
  const response = await fetchBusinessEntities({
    fetchData,
    entityIds,
    entityType: entityType
  });
  return response;
};

export const fetchBusinessEntitiesMap = async (
  entityIds: string[],
  fetchData: any,
  entityType: BusinessEntityType
) => {
  if (!entityIds.length) return {};
  const res = await getBusinessEntities(fetchData, {
    status: true,
    locationIds: entityIds
    //entityType: entityType
  });
  const items: BusinessEntity[] = res?.data ?? [];
  const map: BusinessEntityMap = {};
  for (const e of items) if (e?.id) map[e.id] = e;
  return map;
};
